import React, { useState, useEffect } from 'react';
import { getServices, getForfaits } from '../services/api';
import { Clock, ChevronDown } from 'lucide-react';

const Tarifs = () => {
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [services, setServices] = useState([]);
  const [forfaits, setForfaits] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [servicesData, forfaitsData] = await Promise.all([
          getServices(),
          getForfaits()
        ]);
        setServices(servicesData);
        setForfaits(forfaitsData);
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Grouper les services par catégorie
  const pricingCategories = services.reduce((acc, service) => {
    if (!acc[service.category]) {
      acc[service.category] = [];
    }
    acc[service.category].push(service);
    return acc;
  }, {});

  const toggleCategory = (category) => {
    setExpandedCategory(expandedCategory === category ? null : category);
  };

  if (loading) {
    return (
      <div style={{ padding: '100px 20px', textAlign: 'center' }}>
        <p className="body-large">Chargement des tarifs...</p>
      </div>
    );
  }

  return (
    <div>
      {/* Hero Section */}
      <section 
        style={{
          position: 'relative',
          height: '50vh',
          minHeight: '400px',
          display: 'flex',
          alignItems: 'center',
          overflow: 'hidden',
          background: 'var(--bg-secondary)'
        }}
      >
        <div 
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: 'url(https://images.unsplash.com/photo-1762373349045-c2decd4ec3f3)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: 'brightness(0.5)'
          }}
        />
        
        <div className="container" style={{ position: 'relative', zIndex: 2, maxWidth: '1400px', margin: '0 auto', textAlign: 'center' }}>
          <h1 className="hero-large" style={{ color: 'var(--bg-primary)', marginBottom: '16px' }}>
            Nos Tarifs
          </h1>
          <p className="body-large" style={{ color: 'var(--bg-primary)' }}>
            Transparence et qualité pour tous nos services
          </p>
        </div>
      </section>

      {/* Grille de tarifs */}
      <section className="section-padding" style={{ background: 'var(--bg-primary)' }}>
        <div className="container" style={{ maxWidth: '1000px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            {Object.entries(pricingCategories).map(([categoryName, categoryServices], index) => (
              <div 
                key={index}
                style={{
                  border: '1px solid var(--border-light)',
                  borderRadius: '0px',
                  overflow: 'hidden',
                  background: 'var(--bg-primary)',
                  transition: 'all 0.3s ease'
                }}
              >
                {/* En-tête de catégorie */}
                <button
                  onClick={() => toggleCategory(categoryName)}
                  style={{
                    width: '100%',
                    padding: '24px 32px',
                    background: expandedCategory === categoryName ? 'var(--bg-secondary)' : 'var(--bg-primary)',
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    transition: 'background 0.3s ease'
                  }}
                >
                  <h2 className="heading-2" style={{ color: '#C9A961' }}>
                    {categoryName}
                  </h2>
                  <ChevronDown 
                    size={24} 
                    style={{ 
                      transform: expandedCategory === categoryName ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.3s ease'
                    }}
                  />
                </button>

                {/* Liste des services */}
                {expandedCategory === categoryName && (
                  <div style={{ padding: '0 32px 24px' }}>
                    {categoryServices.map((service, idx) => (
                      <div 
                        key={idx}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '20px 0',
                          borderBottom: idx < categoryServices.length - 1 ? '1px solid var(--border-light)' : 'none',
                          gap: '24px',
                          flexWrap: 'wrap'
                        }}
                      >
                        <div style={{ flex: 1, minWidth: '200px' }}>
                          <h3 className="body-large" style={{ marginBottom: '4px', fontWeight: 400 }}>
                            {service.title}
                          </h3>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Clock size={14} color="var(--text-secondary)" />
                            <span className="body-small" style={{ color: 'var(--text-secondary)' }}>
                              {service.duration}
                            </span>
                          </div>
                        </div>
                        <div>
                          <span className="heading-2" style={{ color: '#C9A961' }}>
                            {service.price}€
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Note importante */}
          <div 
            style={{
              marginTop: '48px',
              padding: '32px',
              background: 'var(--bg-secondary)',
              borderRadius: '0px',
              border: '1px solid var(--border-light)'
            }}
          >
            <h3 className="heading-3" style={{ marginBottom: '16px', color: '#C9A961' }}>
              Informations importantes
            </h3>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              <li className="body-regular" style={{ marginBottom: '12px', paddingLeft: '20px', position: 'relative' }}>
                <span style={{ position: 'absolute', left: 0, color: '#C9A961' }}>•</span>
                Les tarifs sont indicatifs et peuvent varier selon la prestation
              </li>
              <li className="body-regular" style={{ marginBottom: '12px', paddingLeft: '20px', position: 'relative' }}>
                <span style={{ position: 'absolute', left: 0, color: '#C9A961' }}>•</span>
                Paiement en ligne possible lors de la réservation
              </li>
              <li className="body-regular" style={{ marginBottom: '12px', paddingLeft: '20px', position: 'relative' }}>
                <span style={{ position: 'absolute', left: 0, color: '#C9A961' }}>•</span>
                Cartes bancaires et espèces acceptées sur place
              </li>
              <li className="body-regular" style={{ paddingLeft: '20px', position: 'relative' }}>
                <span style={{ position: 'absolute', left: 0, color: '#C9A961' }}>•</span>
                Annulation gratuite jusqu'à 24h avant le rendez-vous
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* CTA Réservation */}
      <section className="section-padding-small" style={{ background: 'var(--bg-secondary)', textAlign: 'center' }}>
        <div className="container" style={{ maxWidth: '800px' }}>
          <h2 className="hero-medium" style={{ marginBottom: '24px' }}>
            Prête à réserver votre soin ?
          </h2>
          <p className="body-large" style={{ color: 'var(--text-secondary)', marginBottom: '40px' }}>
            Choisissez votre plateforme de réservation préférée
          </p>
          <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <a 
              href="https://www.planity.com/nina-care-institut-91360-villemoisson-sur-orge" 
              target="_blank" 
              rel="noopener noreferrer"
              className="btn-primary"
            >
              Réserver sur Planity
            </a>
            <a 
              href="https://www.treatwell.fr/salon/nina-care-institut-1/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="btn-primary"
            >
              Réserver sur Treatwell
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Tarifs;
